const BRASILAPI_BASE_URL = 'https://brasilapi.com.br/api'

export interface CNPJData {
  cnpj: string
  razao_social: string
  nome_fantasia: string
  descricao_situacao_cadastral: string
  data_situacao_cadastral: string
  data_inicio_atividade: string
  cnae_fiscal: string
  cnae_fiscal_descricao: string
  tipo_logradouro: string
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  cep: string
  uf: string
  municipio: string
  ddd_telefone_1: string
  ddd_telefone_2?: string
  email?: string
  capital_social: number
  porte: string
  natureza_juridica: string
}

/**
 * Lookup CNPJ data from BrasilAPI
 */
export async function lookupCNPJ(cnpj: string): Promise<CNPJData> {
  // Remove formatting (dots, slashes, dashes)
  const cleanCNPJ = cnpj.replace(/[^\d]/g, '')

  if (cleanCNPJ.length !== 14) {
    throw new Error('CNPJ deve ter 14 dígitos')
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 seconds

  try {
    const response = await fetch(
      `${BRASILAPI_BASE_URL}/cnpj/v1/${cleanCNPJ}`,
      {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        },
      }
    )

    clearTimeout(timeoutId)

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('CNPJ não encontrado')
      } else if (response.status === 429) {
        throw new Error('Muitas requisições. Tente novamente em alguns segundos.')
      } else {
        throw new Error('Erro ao buscar CNPJ. Tente novamente.')
      }
    }

    const data: CNPJData = await response.json()
    return data
  } catch (error: any) {
    clearTimeout(timeoutId)

    if (error.name === 'AbortError') {
      throw new Error('Timeout: BrasilAPI demorou muito para responder')
    }

    // Re-throw if it's already our custom error
    if (error.message.includes('CNPJ')) {
      throw error
    }

    throw new Error('Erro ao buscar CNPJ. Verifique sua conexão.')
  }
}

/**
 * Format CNPJ: 12345678000199 -> 12.345.678/0001-99
 */
export function formatCNPJ(cnpj: string): string {
  const clean = cnpj.replace(/[^\d]/g, '')

  if (clean.length !== 14) {
    return cnpj
  }

  return clean.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  )
}

/**
 * Validate CNPJ format (basic validation)
 */
export function isValidCNPJFormat(cnpj: string): boolean {
  const clean = cnpj.replace(/[^\d]/g, '')
  return clean.length === 14
}

/**
 * Format address from CNPJ data
 */
export function formatAddressFromCNPJ(data: CNPJData) {
  return {
    street: data.tipo_logradouro
      ? `${data.tipo_logradouro} ${data.logradouro}`.trim()
      : data.logradouro,
    number: data.numero,
    complement: data.complemento || undefined,
    neighborhood: data.bairro,
    city: data.municipio,
    state: data.uf,
    zipCode: data.cep,
    country: 'Brasil',
  }
}
